import { Injectable, InjectionToken, Inject, Optional } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpEventType, HttpEvent } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, throwError, of } from 'rxjs';
import { map, catchError, tap, retry, delay, takeUntil, timeout } from 'rxjs/operators';
import {
  FileVaultItem,
  FileHistory,
  DirectoryNode,
  PagedResult,
  FileSearchCriteria,
  ChunkUploadInfo,
  FileChunk,
  UploadStatus,
  UploadOptions,
  UploadInitResponse,
  ChunkUploadResponse,
  UploadCompleteResponse,
  PnaPart,
  CreateDrawingLogRequest,
  UpdateDrawingLogRequest,
  DrawingNumberResult,
  LifecycleStateInfo,
  LifecycleTransition,
  LifecycleHistoryItem,
  LifecycleTransitionResponse,
  LifecycleStateMachineEntry,
  LifecycleState,
  BomNode,
  BomLineItem,
  BomDifference,
  BomQueryOptions,
  AddBomChildRequest,
  UpdateBomChildRequest,
  Part,
  PartRelationship,
  BomValidationResult,
  CreatePartRequest,
  CreateDocumentRequest,
  Document,
  DocumentVersion,
  LinkDocumentRequest,
  PartDocumentLink,
  LinkDocumentsToPartRequest,
  LinkDocumentsToPartResponse,
  BomDocumentLinkRequest,
  DocumentLinkDetail,
  PartVersion,
  Change,
  ChangeItem,
  ChangeSearchCriteria,
  CreateChangeRequest,
  UpdateChangeRequest,
  AddChangeItemRequest,
  UpdateChangeItemRequest,
  ChangeTransitionResult,
  SubmitChangeRequest,
  ApproveChangeRequest,
  RejectChangeRequest,
  StartImplementationRequest,
  ImplementChangeRequest,
  CloseChangeRequest,
  ConvertToEcoRequest,
  ChangeState,
  Notification,
  NotificationSearchCriteria,
  NotificationSearchResult,
  NotificationSummary,
  UserNotificationPreference,
  UpdateNotificationPreferencesRequest,
  Webhook,
  WebhookDelivery,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  NotificationTemplate,
  SendNotificationRequest,
  MarkNotificationsReadRequest,
  CommonUser,
  CommonUserDetail,
  CommonRole,
  CommonPermission,
  UpdateUserRoleRequest,
  UpdateUserPermissionsRequest,
  SearchCriteria,
  SearchResults,
  SearchSuggestion,
  RecentSearch,
  SearchEntityType,
  SavedSearch,
  WorkflowTemplate,
  WorkflowInstance,
  WorkflowStep,
  WorkflowTask,
  WorkflowProgress,
  WorkflowStepResult,
  StartWorkflowResult,
  WorkflowSearchCriteria,
  WorkflowTaskSearchCriteria,
  WorkflowState,
  WorkflowStepState,
  CreateWorkflowTemplateRequest,
  UpdateWorkflowTemplateRequest,
  StartWorkflowRequest,
  ApproveStepRequest,
  RejectStepRequest,
  DelegateStepRequest,
  SkipStepRequest,
  ClaimStepRequest,
  CancelWorkflowRequest,
  AddWorkflowStepRequest,
  RemoveWorkflowStepRequest,
  ApplicationTypeInfo,
  ApplicationFileInfo,
  ApplicationFileVisibilityConfig,
  DocumentUploadResult
} from '../models';

export const FILEVAULT_API_URL = new InjectionToken<string>('FILEVAULT_API_URL');

export interface FileVaultConfig {
  apiUrl: string;
  defaultChunkSize?: number; // in bytes, default 5MB
  maxConcurrentChunks?: number;
  retryAttempts?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FileVaultApiService {
  private readonly defaultChunkSize = 5 * 1024 * 1024; // 5MB
  private readonly maxConcurrentChunks = 3;
  private readonly retryAttempts = 3;
  
  private activeUploads = new Map<string, ChunkUploadInfo>();
  private uploadProgress$ = new Map<string, BehaviorSubject<ChunkUploadInfo>>();
  private cancelTokens = new Map<string, Subject<void>>();

  constructor(
    private http: HttpClient,
    @Optional() @Inject(FILEVAULT_API_URL) private apiUrl: string
  ) {
    this.apiUrl = apiUrl || '/api/v1';
  }

  // ============ File Operations ============

  /**
   * Search files with criteria
   */
  searchFiles(criteria: FileSearchCriteria): Observable<PagedResult<FileVaultItem>> {
    let params = new HttpParams();
    
    if (criteria.searchText) params = params.set('searchText', criteria.searchText);
    if (criteria.legacyId) params = params.set('legacyId', criteria.legacyId.toString());
    if (criteria.fileName) params = params.set('fileName', criteria.fileName);
    if (criteria.fileExtension) params = params.set('fileExtension', criteria.fileExtension);
    if (criteria.directory) params = params.set('directory', criteria.directory);
    if (criteria.targetId) params = params.set('targetId', criteria.targetId.toString());
    if (criteria.applicationType !== undefined) params = params.set('applicationType', criteria.applicationType.toString());
    if (criteria.isLocked !== undefined) params = params.set('isLocked', criteria.isLocked.toString());
    if (criteria.isObsolete !== undefined) params = params.set('isObsolete', criteria.isObsolete.toString());
    if (criteria.page) params = params.set('page', criteria.page.toString());
    if (criteria.pageSize) params = params.set('pageSize', criteria.pageSize.toString());
    if (criteria.sortBy) params = params.set('sortBy', criteria.sortBy);
    if (criteria.sortDescending !== undefined) params = params.set('sortDescending', criteria.sortDescending.toString());

    return this.http.get<PagedResult<FileVaultItem>>(`${this.apiUrl}/Files`, { params });
  }

  /**
   * Get file by GUID
   */
  getFile(id: string): Observable<FileVaultItem> {
    return this.http.get<FileVaultItem>(`${this.apiUrl}/Files/${id}`);
  }

  /**
   * Get file by legacy ID
   */
  getFileByLegacyId(legacyId: number): Observable<FileVaultItem> {
    return this.http.get<FileVaultItem>(`${this.apiUrl}/Files/by-legacy-id/${legacyId}`);
  }

  /**
   * Get files by target ID (e.g., RTA number)
   */
  getFilesByTargetId(targetId: number, applicationType?: number): Observable<PagedResult<FileVaultItem>> {
    return this.searchFiles({
      targetId,
      applicationType,
      pageSize: 1000
    });
  }

  /**
   * Download file content
   */
  downloadFile(id: string, version?: number): Observable<Blob> {
    let url = `${this.apiUrl}/Files/${id}/download`;
    if (version !== undefined) {
      url += `?version=${version}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Get file version history
   */
  getFileHistory(id: string): Observable<FileHistory[]> {
    return this.http.get<FileHistory[]>(`${this.apiUrl}/Files/${id}/history`);
  }

  /**
   * Check out a file
   */
  checkOutFile(id: string, notes?: string): Observable<FileVaultItem> {
    return this.http.post<FileVaultItem>(`${this.apiUrl}/Files/${id}/checkout`, { notes });
  }

  /**
   * Check in a file
   */
  checkInFile(id: string, file: File, description?: string, revisionNotes?: string, createNewRevision = true): Observable<FileVaultItem> {
    const formData = new FormData();
    formData.append('File', file);
    if (description) formData.append('Description', description);
    if (revisionNotes) formData.append('RevisionNotes', revisionNotes);
    formData.append('CreateNewRevision', createNewRevision.toString());

    return this.http.post<FileVaultItem>(`${this.apiUrl}/Files/${id}/checkin`, formData);
  }

  /**
   * Undo checkout
   */
  undoCheckOut(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/Files/${id}/undo-checkout`, {});
  }

  /**
   * Move file to new location
   */
  moveFile(id: string, destinationPath: string): Observable<FileVaultItem> {
    return this.http.post<FileVaultItem>(`${this.apiUrl}/Files/${id}/move`, { destinationPath });
  }

  /**
   * Delete (soft-delete/obsolete) a file
   */
  deleteFile(id: string, permanent = false): Observable<void> {
    const params = permanent ? '?permanent=true' : '';
    return this.http.delete<void>(`${this.apiUrl}/Files/${id}${params}`);
  }

  /**
   * Copy file
   */
  copyFile(id: string, destinationPath: string): Observable<FileVaultItem> {
    return this.http.post<FileVaultItem>(`${this.apiUrl}/Files/${id}/copy`, { destinationPath });
  }

  /**
   * Restore file to previous version
   */
  restoreVersion(id: string, version: number): Observable<FileVaultItem> {
    return this.http.post<FileVaultItem>(`${this.apiUrl}/Files/${id}/restore/${version}`, {});
  }

  // ============ Application Type & Application File Operations ============

  /**
   * Get all active application types
   */
  getApplicationTypes(): Observable<ApplicationTypeInfo[]> {
    return this.http.get<ApplicationTypeInfo[]>(`${this.apiUrl}/ApplicationTypes`);
  }

  /**
   * Get an application type by ID
   */
  getApplicationType(id: number): Observable<ApplicationTypeInfo> {
    return this.http.get<ApplicationTypeInfo>(`${this.apiUrl}/ApplicationTypes/${id}`);
  }

  /**
   * Get an application type by name (e.g., "Rta", "Ejdf", "Application").
   * Returns the full ApplicationTypeInfo including the numeric ID.
   */
  getApplicationTypeByName(name: string): Observable<ApplicationTypeInfo> {
    return this.http.get<ApplicationTypeInfo>(`${this.apiUrl}/ApplicationTypes/by-name/${encodeURIComponent(name)}`);
  }

  /**
   * Get all files for a specific application type and target
   */
  getApplicationFiles(applicationTypeId: number, targetId: number): Observable<ApplicationFileInfo[]> {
    return this.http.get<ApplicationFileInfo[]>(
      `${this.apiUrl}/ApplicationTypes/${applicationTypeId}/targets/${targetId}/files`
    );
  }

  /**
   * Get files for a target with cross-application-type visibility rules.
   * The owner app type sees all its files.
   * Additional app type IDs only return files where isGlobalView = true.
   *
   * @example
   * // RTA page: sees all RTA files + only isGlobalView EJDF files
   * getApplicationFilesWithVisibility({ ownerApplicationTypeId: 4, additionalApplicationTypeIds: [2], targetId: 12345 })
   *
   * // EJDF page: sees all EJDF files + all RTA files (RTA files uploaded with isGlobalView behavior)
   * getApplicationFilesWithVisibility({ ownerApplicationTypeId: 2, additionalApplicationTypeIds: [4], targetId: 12345 })
   */
  getApplicationFilesWithVisibility(config: ApplicationFileVisibilityConfig): Observable<ApplicationFileInfo[]> {
    let params = new HttpParams()
      .set('ownerApplicationTypeId', config.ownerApplicationTypeId.toString());

    if (config.additionalApplicationTypeIds && config.additionalApplicationTypeIds.length > 0) {
      params = params.set('additionalApplicationTypeIds', config.additionalApplicationTypeIds.join(','));
    }

    return this.http.get<ApplicationFileInfo[]>(
      `${this.apiUrl}/ApplicationTypes/targets/${config.targetId}/files`,
      { params }
    );
  }

  /**
   * Get all application file links for a specific file
   */
  getApplicationFileLinks(fileId: string): Observable<ApplicationFileInfo[]> {
    return this.http.get<ApplicationFileInfo[]>(`${this.apiUrl}/ApplicationTypes/files/${fileId}`);
  }

  /**
   * Remove (soft-delete) an application file link
   */
  removeApplicationFileLink(applicationFileId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/ApplicationTypes/application-files/${applicationFileId}`
    );
  }

  /**
   * Toggle the IsGlobalView flag on an application file link
   */
  toggleApplicationFileGlobalView(applicationFileId: number): Observable<ApplicationFileInfo> {
    return this.http.patch<ApplicationFileInfo>(
      `${this.apiUrl}/ApplicationTypes/application-files/${applicationFileId}/toggle-global-view`,
      {}
    );
  }

  // ============ Directory Operations ============

  /**
   * Get directory tree
   */
  getDirectoryTree(rootPath?: string, depth = 2): Observable<DirectoryNode[]> {
    let params = new HttpParams();
    if (rootPath) params = params.set('path', rootPath);
    params = params.set('depth', depth.toString());
    
    return this.http.get<DirectoryNode[]>(`${this.apiUrl}/Directories/tree`, { params });
  }

  /**
   * Get directory contents
   */
  getDirectoryContents(path: string): Observable<{ directories: DirectoryNode[]; files: FileVaultItem[] }> {
    return this.http.get<{ directories: DirectoryNode[]; files: FileVaultItem[] }>(
      `${this.apiUrl}/Directories/contents`,
      { params: new HttpParams().set('path', path) }
    );
  }

  /**
   * Create directory
   */
  createDirectory(path: string): Observable<DirectoryNode> {
    return this.http.post<DirectoryNode>(`${this.apiUrl}/Directories`, { path });
  }

  // ============ Part Operations ============

  /**
   * Get part by part number (SAP material number)
   */
  getPartByNumber(partNumber: string): Observable<Part> {
    return this.http.get<Part>(`${this.apiUrl}/Parts/by-number/${encodeURIComponent(partNumber)}`);
  }

  /**
   * Get part by ID (GUID)
   */
  getPart(id: string): Observable<Part> {
    return this.http.get<Part>(`${this.apiUrl}/Parts/${id}`);
  }

  /**
   * Get document by ID (GUID)
   */
  getDocument(id: string): Observable<Document> {
    return this.http.get<Document>(`${this.apiUrl}/Documents/${id}`);
  }

  // ============ Drawing Number Operations ============

  /**
   * Get PNA parts for drawing number requests
   */
  getPnaParts(subPsl?: number, showAll = false): Observable<PnaPart[]> {
    let params = new HttpParams();
    if (subPsl !== undefined && subPsl !== null) {
      params = params.set('subPsl', subPsl.toString());
    }
    if (showAll) {
      params = params.set('showAll', 'true');
    }
    return this.http.get<PnaPart[]>(`${this.apiUrl}/DrawingNumbers/pna-parts`, { params });
  }

  /**
   * Create a drawing log entry (returns drawing number)
   */
  createDrawingLog(request: CreateDrawingLogRequest): Observable<DrawingNumberResult> {
    return this.http.post<DrawingNumberResult>(`${this.apiUrl}/DrawingNumbers`, request);
  }

  /**
   * Update a drawing log entry
   */
  updateDrawingLog(drawingLogId: number, request: UpdateDrawingLogRequest): Observable<DrawingNumberResult> {
    return this.http.patch<DrawingNumberResult>(`${this.apiUrl}/DrawingNumbers/${drawingLogId}`, request);
  }

  /**
   * Get drawing numbers for an RTA number
   */
  getDrawingNumbersByRta(rtaNum: number): Observable<DrawingNumberResult[]> {
    return this.http.get<DrawingNumberResult[]>(`${this.apiUrl}/DrawingNumbers/rta/${rtaNum}`);
  }

  // ============ Chunked Upload Operations ============

  /**
   * Upload a file with chunking support for large files
   */
  uploadFileChunked(file: File, options: UploadOptions = {}): Observable<ChunkUploadInfo> {
    const chunkSize = options.chunkSize || this.defaultChunkSize;
    const uploadId = this.generateUploadId();
    const totalChunks = Math.ceil(file.size / chunkSize);

    const uploadInfo: ChunkUploadInfo = {
      fileId: uploadId,
      fileName: file.name,
      fileSize: file.size,
      chunkSize,
      totalChunks,
      uploadedChunks: 0,
      status: UploadStatus.Pending,
      progress: 0,
      startTime: new Date()
    };

    // Create progress subject
    const progress$ = new BehaviorSubject<ChunkUploadInfo>(uploadInfo);
    this.uploadProgress$.set(uploadId, progress$);
    this.activeUploads.set(uploadId, uploadInfo);
    
    // Create cancel token
    const cancelToken = new Subject<void>();
    this.cancelTokens.set(uploadId, cancelToken);

    // Start upload process
    if (options.autoStart !== false) {
      this.startChunkedUpload(file, uploadId, options, cancelToken);
    }

    return progress$.asObservable();
  }

  /**
   * Start or resume chunked upload
   */
  private async startChunkedUpload(file: File, uploadId: string, options: UploadOptions, cancelToken: Subject<void>): Promise<void> {
    const uploadInfo = this.activeUploads.get(uploadId);
    if (!uploadInfo) return;

    uploadInfo.status = UploadStatus.Uploading;
    this.updateProgress(uploadId, uploadInfo);

    const chunkSize = uploadInfo.chunkSize;
    const totalChunks = uploadInfo.totalChunks;
    const maxConcurrent = options.maxConcurrentChunks || this.maxConcurrentChunks;
    const retryAttempts = options.retryAttempts || this.retryAttempts;

    try {
      // Initialize upload on server
      const initResponse = await this.initializeUpload(file, uploadId, options).toPromise();
      
      // Upload chunks with concurrency control
      const chunkPromises: Promise<void>[] = [];
      let currentChunk = uploadInfo.uploadedChunks;

      while (currentChunk < totalChunks) {
        // Get current status (may change during upload)
        const currentStatus = this.activeUploads.get(uploadId)?.status;
        
        // Check for cancellation
        if (currentStatus === UploadStatus.Cancelled) {
          throw new Error('Upload cancelled');
        }

        // Check for pause
        if (currentStatus === UploadStatus.Paused) {
          await this.waitForResume(uploadId);
        }

        // Limit concurrent uploads
        if (chunkPromises.length >= maxConcurrent) {
          await Promise.race(chunkPromises);
        }

        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const chunkData: FileChunk = {
          uploadId,
          chunkIndex: currentChunk,
          totalChunks,
          data: chunk,
          fileName: file.name,
          fileSize: file.size
        };

        const uploadPromise = this.uploadChunk(chunkData, retryAttempts)
          .then(() => {
            uploadInfo.uploadedChunks++;
            uploadInfo.progress = (uploadInfo.uploadedChunks / totalChunks) * 100;
            uploadInfo.lastChunkTime = new Date();
            this.updateProgress(uploadId, uploadInfo);
          })
          .finally(() => {
            const index = chunkPromises.indexOf(uploadPromise);
            if (index > -1) chunkPromises.splice(index, 1);
          });

        chunkPromises.push(uploadPromise);
        currentChunk++;
      }

      // Wait for all remaining chunks
      await Promise.all(chunkPromises);

      // Complete upload
      const completeResponse = await this.completeUpload(uploadId).toPromise();
      
      uploadInfo.status = UploadStatus.Completed;
      uploadInfo.progress = 100;
      if (completeResponse) {
        uploadInfo.fileId = completeResponse.fileId;
      }
      this.updateProgress(uploadId, uploadInfo);

    } catch (error: any) {
      uploadInfo.status = UploadStatus.Failed;
      this.parseAndSetError(uploadInfo, error);
      this.updateProgress(uploadId, uploadInfo);
    }
  }

  /**
   * Initialize upload on server
   */
  private initializeUpload(file: File, uploadId: string, options: UploadOptions): Observable<UploadInitResponse> {
    return this.http.post<UploadInitResponse>(`${this.apiUrl}/Files/upload/init`, {
      uploadId,
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
      targetId: options.targetId,
      applicationType: options.applicationType,
      description: options.description,
      metadata: options.metadata,
      subPslName: options.subPslName || undefined
    });
  }

  /**
   * Upload a single chunk with retry logic
   */
  private async uploadChunk(chunk: FileChunk, retryAttempts: number): Promise<ChunkUploadResponse> {
    const formData = new FormData();
    formData.append('uploadId', chunk.uploadId);
    formData.append('chunkIndex', chunk.chunkIndex.toString());
    formData.append('totalChunks', chunk.totalChunks.toString());
    formData.append('chunk', chunk.data, chunk.fileName);

    let lastError: any;
    
    for (let attempt = 0; attempt <= retryAttempts; attempt++) {
      try {
        const response = await this.http.post<ChunkUploadResponse>(
          `${this.apiUrl}/Files/upload/chunk`,
          formData
        ).toPromise();
        
        if (response) return response;
      } catch (error) {
        lastError = error;
        if (attempt < retryAttempts) {
          await this.delay(1000 * (attempt + 1)); // Exponential backoff
        }
      }
    }

    throw lastError || new Error('Chunk upload failed');
  }

  /**
   * Complete the upload (generous timeout for large-file assembly on the server)
   */
  private completeUpload(uploadId: string): Observable<UploadCompleteResponse> {
    return this.http.post<UploadCompleteResponse>(
      `${this.apiUrl}/Files/upload/complete`,
      { uploadId }
    ).pipe(
      timeout(10 * 60 * 1000) // 10 minutes — server-side StageBlock/CommitBlockList can be slow for 100MB+ files
    );
  }

  /**
   * Pause an active upload
   */
  pauseUpload(uploadId: string): void {
    const uploadInfo = this.activeUploads.get(uploadId);
    if (uploadInfo && uploadInfo.status === UploadStatus.Uploading) {
      uploadInfo.status = UploadStatus.Paused;
      this.updateProgress(uploadId, uploadInfo);
    }
  }

  /**
   * Resume a paused upload
   */
  resumeUpload(uploadId: string): void {
    const uploadInfo = this.activeUploads.get(uploadId);
    if (uploadInfo && uploadInfo.status === UploadStatus.Paused) {
      uploadInfo.status = UploadStatus.Uploading;
      this.updateProgress(uploadId, uploadInfo);
    }
  }

  /**
   * Cancel an upload
   */
  cancelUpload(uploadId: string): void {
    const uploadInfo = this.activeUploads.get(uploadId);
    if (uploadInfo) {
      uploadInfo.status = UploadStatus.Cancelled;
      this.updateProgress(uploadId, uploadInfo);
      
      const cancelToken = this.cancelTokens.get(uploadId);
      if (cancelToken) {
        cancelToken.next();
        cancelToken.complete();
      }

      // Notify server to cleanup
      this.http.delete(`${this.apiUrl}/Files/upload/${uploadId}`).subscribe();
    }
  }

  /**
   * Get upload progress
   */
  getUploadProgress(uploadId: string): Observable<ChunkUploadInfo> | undefined {
    return this.uploadProgress$.get(uploadId)?.asObservable();
  }

  /**
   * Get all active uploads
   */
  getActiveUploads(): ChunkUploadInfo[] {
    return Array.from(this.activeUploads.values());
  }

  // ============ Helper Methods ============

  /**
   * Parses an HttpErrorResponse or plain Error and sets structured error fields
   * on the ChunkUploadInfo for display in the UI.
   */
  private parseAndSetError(uploadInfo: ChunkUploadInfo, err: any): void {
    // HttpErrorResponse (Angular HttpClient)
    if (err?.status !== undefined) {
      const status: number = err.status;
      const body = err.error;

      switch (status) {
        case 0:
          uploadInfo.errorCode = 'NETWORK_ERROR';
          // status 0 can be: network drop, CORS block, request timeout, or server abort
          if (err?.statusText === 'Unknown Error' && err?.url) {
            const failedEndpoint = err.url.split('/').pop() || 'unknown';
            const elapsed = uploadInfo.startTime
              ? Math.round((Date.now() - new Date(uploadInfo.startTime).getTime()) / 1000)
              : null;
            const elapsedStr = elapsed !== null ? ` after ${elapsed}s` : '';
            const chunkProgress = `${uploadInfo.uploadedChunks}/${uploadInfo.totalChunks} chunks uploaded`;

            if (failedEndpoint === 'complete') {
              uploadInfo.error = `Server did not respond to the assembly request${elapsedStr}. `
                + `All ${chunkProgress} were sent successfully, but the server may have timed out assembling the final file. `
                + `This typically happens with files >100MB. Check server logs for details.`;
            } else if (failedEndpoint === 'chunk') {
              uploadInfo.error = `Lost connection while uploading chunk data${elapsedStr} (${chunkProgress}). `
                + `The request may have exceeded the server's request size or timeout limit.`;
            } else if (failedEndpoint === 'init') {
              uploadInfo.error = `Could not initialize the upload session. The server may be unreachable.`;
            } else {
              uploadInfo.error = `Request to '${failedEndpoint}' failed with no response${elapsedStr} (${chunkProgress}).`;
            }
          } else {
            uploadInfo.error = 'Could not reach the server. Check your network connection.';
          }
          uploadInfo.errorDetails = [
            `HTTP Status: 0 (no response received)`,
            `URL: ${err?.url || 'unknown'}`,
            `Status Text: ${err?.statusText || 'N/A'}`,
            `Message: ${err?.message || 'N/A'}`,
            `Uploaded: ${uploadInfo.uploadedChunks}/${uploadInfo.totalChunks} chunks`,
            `File Size: ${(uploadInfo.fileSize / (1024 * 1024)).toFixed(1)} MB`
          ];
          break;
        case 400:
          uploadInfo.errorCode = 'VALIDATION_ERROR';
          uploadInfo.error = body?.message || body?.title || 'The file was rejected by the server.';
          if (body?.errors) {
            uploadInfo.errorDetails = ([] as string[]).concat(...Object.values(body.errors) as string[][]);
          }
          break;
        case 401:
          uploadInfo.errorCode = 'UNAUTHORIZED';
          uploadInfo.error = 'Your session has expired. Please log in again.';
          break;
        case 403:
          uploadInfo.errorCode = 'FORBIDDEN';
          uploadInfo.error = 'You do not have permission to upload files here.';
          break;
        case 404:
          uploadInfo.errorCode = 'SESSION_EXPIRED';
          uploadInfo.error = 'Upload session not found. The session may have expired — please try again.';
          break;
        case 409:
          uploadInfo.errorCode = 'CONFLICT';
          uploadInfo.error = body?.message || 'A file with this name is currently checked out by another user.';
          break;
        case 413:
          uploadInfo.errorCode = 'FILE_TOO_LARGE';
          uploadInfo.error = body?.message || 'File exceeds the maximum allowed size configured on the server.';
          break;
        case 415:
          uploadInfo.errorCode = 'UNSUPPORTED_TYPE';
          uploadInfo.error = body?.message || 'This file type is not allowed.';
          break;
        case 500:
          uploadInfo.errorCode = 'SERVER_ERROR';
          uploadInfo.error = body?.message || body?.error || 'An unexpected server error occurred. Please try again or contact support.';
          break;
        case 503:
          uploadInfo.errorCode = 'SERVICE_UNAVAILABLE';
          uploadInfo.error = 'The service is temporarily unavailable. Please try again shortly.';
          break;
        default:
          uploadInfo.errorCode = `HTTP_${status}`;
          uploadInfo.error = body?.message || body?.error || `Upload failed with status ${status}.`;
      }
    } else if (err?.name === 'TimeoutError') {
      // RxJS timeout operator fired (e.g. completeUpload took too long)
      uploadInfo.errorCode = 'TIMEOUT';
      const elapsed = uploadInfo.startTime
        ? Math.round((Date.now() - new Date(uploadInfo.startTime).getTime()) / 1000)
        : null;
      uploadInfo.error = `The server did not respond within the allowed time${elapsed ? ` (${elapsed}s elapsed)` : ''}. `
        + `All ${uploadInfo.uploadedChunks}/${uploadInfo.totalChunks} chunks were sent, but the final assembly timed out. `
        + `The file may still be processing on the server — check server logs before retrying.`;
      uploadInfo.errorDetails = [
        `Error Type: RxJS TimeoutError`,
        `Uploaded: ${uploadInfo.uploadedChunks}/${uploadInfo.totalChunks} chunks`,
        `File Size: ${(uploadInfo.fileSize / (1024 * 1024)).toFixed(1)} MB`
      ];
    } else {
      // Plain JS/runtime error
      uploadInfo.errorCode = 'CLIENT_ERROR';
      uploadInfo.error = err?.message || 'An unexpected error occurred while preparing the file.';
    }
  }

  private updateProgress(uploadId: string, info: ChunkUploadInfo): void {
    const subject = this.uploadProgress$.get(uploadId);
    if (subject) {
      subject.next({ ...info });
    }
  }

  private generateUploadId(): string {
    return 'upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async waitForResume(uploadId: string): Promise<void> {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        const uploadInfo = this.activeUploads.get(uploadId);
        if (!uploadInfo || uploadInfo.status !== UploadStatus.Paused) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 500);
    });
  }

  // ============ Lifecycle Operations ============

  /**
   * Get current lifecycle state of an entity
   */
  getLifecycleState(entityType: string, entityId: string): Observable<LifecycleStateInfo> {
    return this.http.get<LifecycleStateInfo>(`${this.apiUrl}/Lifecycle/${entityType}/${entityId}/state`);
  }

  /**
   * Get allowed transitions for an entity
   */
  getAllowedTransitions(entityType: string, entityId: string): Observable<LifecycleTransition[]> {
    return this.http.get<LifecycleTransition[]>(`${this.apiUrl}/Lifecycle/${entityType}/${entityId}/transitions`);
  }

  /**
   * Check if a specific transition is allowed
   */
  canTransition(entityType: string, entityId: string, targetState: LifecycleState): Observable<{ canTransition: boolean; targetState: LifecycleState }> {
    const params = new HttpParams().set('targetState', targetState.toString());
    return this.http.get<{ canTransition: boolean; targetState: LifecycleState }>(
      `${this.apiUrl}/Lifecycle/${entityType}/${entityId}/can-transition`,
      { params }
    );
  }

  /**
   * Check if an entity can be modified
   */
  canModify(entityType: string, entityId: string): Observable<{ canModify: boolean }> {
    return this.http.get<{ canModify: boolean }>(`${this.apiUrl}/Lifecycle/${entityType}/${entityId}/can-modify`);
  }

  /**
   * Promote an entity to a higher lifecycle state
   */
  promoteLifecycle(entityType: string, entityId: string, targetState: LifecycleState, notes?: string): Observable<LifecycleTransitionResponse> {
    return this.http.post<LifecycleTransitionResponse>(
      `${this.apiUrl}/Lifecycle/${entityType}/${entityId}/promote`,
      { targetState, notes }
    );
  }

  /**
   * Demote an entity to a lower lifecycle state
   */
  demoteLifecycle(entityType: string, entityId: string, targetState: LifecycleState, reason: string): Observable<LifecycleTransitionResponse> {
    return this.http.post<LifecycleTransitionResponse>(
      `${this.apiUrl}/Lifecycle/${entityType}/${entityId}/demote`,
      { targetState, reason }
    );
  }

  /**
   * Mark an entity as obsolete
   */
  obsoleteLifecycle(entityType: string, entityId: string, reason: string): Observable<LifecycleTransitionResponse> {
    return this.http.post<LifecycleTransitionResponse>(
      `${this.apiUrl}/Lifecycle/${entityType}/${entityId}/obsolete`,
      { reason }
    );
  }

  /**
   * Get lifecycle history for an entity
   */
  getLifecycleHistory(entityType: string, entityId: string): Observable<LifecycleHistoryItem[]> {
    return this.http.get<LifecycleHistoryItem[]>(`${this.apiUrl}/Lifecycle/${entityType}/${entityId}/history`);
  }

  /**
   * Get the latest lifecycle transition
   */
  getLatestTransition(entityType: string, entityId: string): Observable<LifecycleHistoryItem> {
    return this.http.get<LifecycleHistoryItem>(`${this.apiUrl}/Lifecycle/${entityType}/${entityId}/history/latest`);
  }

  /**
   * Get the lifecycle state machine configuration
   */
  getStateMachine(): Observable<LifecycleStateMachineEntry[]> {
    return this.http.get<LifecycleStateMachineEntry[]>(`${this.apiUrl}/Lifecycle/state-machine`);
  }

  /**
   * Get all lifecycle states
   */
  getLifecycleStates(): Observable<{ name: string; value: number; isReleased: boolean; isEditable: boolean; isFinal: boolean }[]> {
    return this.http.get<{ name: string; value: number; isReleased: boolean; isEditable: boolean; isFinal: boolean }[]>(
      `${this.apiUrl}/Lifecycle/states`
    );
  }

  /**
   * Get valid entity types for lifecycle management
   */
  getLifecycleEntityTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/Lifecycle/entity-types`);
  }

  // ============ BOM Operations ============

  /**
   * Get BOM (children) for a part
   */
  getBom(partId: string, options?: BomQueryOptions): Observable<BomNode[]> {
    let params = new HttpParams();
    if (options?.maxDepth !== undefined) params = params.set('maxDepth', options.maxDepth.toString());
    if (options?.effectiveDate) params = params.set('effectiveDate', options.effectiveDate.toISOString());
    if (options?.includeInactive !== undefined) params = params.set('includeInactive', options.includeInactive.toString());

    return this.http.get<BomNode[]>(`${this.apiUrl}/Bom/parts/${partId}`, { params });
  }

  /**
   * Get BOM for a specific part version
   */
  getBomForVersion(versionId: string, options?: BomQueryOptions): Observable<BomNode[]> {
    let params = new HttpParams();
    if (options?.maxDepth !== undefined) params = params.set('maxDepth', options.maxDepth.toString());
    if (options?.effectiveDate) params = params.set('effectiveDate', options.effectiveDate.toISOString());
    if (options?.includeInactive !== undefined) params = params.set('includeInactive', options.includeInactive.toString());

    return this.http.get<BomNode[]>(`${this.apiUrl}/Bom/versions/${versionId}`, { params });
  }

  /**
   * Get flattened BOM as a list of line items
   */
  getFlattenedBom(partId: string, options?: BomQueryOptions): Observable<BomLineItem[]> {
    let params = new HttpParams();
    if (options?.maxDepth !== undefined) params = params.set('maxDepth', options.maxDepth.toString());
    if (options?.effectiveDate) params = params.set('effectiveDate', options.effectiveDate.toISOString());
    if (options?.includeInactive !== undefined) params = params.set('includeInactive', options.includeInactive.toString());

    return this.http.get<BomLineItem[]>(`${this.apiUrl}/Bom/parts/${partId}/flat`, { params });
  }

  /**
   * Get where-used (parents) for a part
   */
  getWhereUsed(partId: string, options?: BomQueryOptions): Observable<BomNode[]> {
    let params = new HttpParams();
    if (options?.maxDepth !== undefined) params = params.set('maxDepth', options.maxDepth.toString());
    if (options?.effectiveDate) params = params.set('effectiveDate', options.effectiveDate.toISOString());
    if (options?.includeInactive !== undefined) params = params.set('includeInactive', options.includeInactive.toString());

    return this.http.get<BomNode[]>(`${this.apiUrl}/Bom/parts/${partId}/where-used`, { params });
  }

  /**
   * Add a child part to a parent's BOM
   */
  addBomChild(parentPartId: string, request: AddBomChildRequest): Observable<PartRelationship> {
    return this.http.post<PartRelationship>(`${this.apiUrl}/Bom/parts/${parentPartId}/children`, request);
  }

  /**
   * Add a child part to a specific version's BOM
   */
  addBomChildToVersion(parentVersionId: string, request: AddBomChildRequest): Observable<PartRelationship> {
    return this.http.post<PartRelationship>(`${this.apiUrl}/Bom/versions/${parentVersionId}/children`, request);
  }

  /**
   * Update a BOM relationship
   */
  updateBomChild(relationshipId: string, request: UpdateBomChildRequest): Observable<PartRelationship> {
    return this.http.put<PartRelationship>(`${this.apiUrl}/Bom/relationships/${relationshipId}`, request);
  }

  /**
   * Update quantity for a BOM line
   */
  updateBomQuantity(relationshipId: string, quantity: number): Observable<PartRelationship> {
    return this.http.patch<PartRelationship>(`${this.apiUrl}/Bom/relationships/${relationshipId}/quantity`, { quantity });
  }

  /**
   * Remove a child from the BOM
   */
  removeBomChild(relationshipId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Bom/relationships/${relationshipId}`);
  }

  /**
   * Reorder BOM children
   */
  reorderBomChildren(parentPartId: string, relationshipIds: string[]): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(`${this.apiUrl}/Bom/parts/${parentPartId}/reorder`, { relationshipIds });
  }

  /**
   * Compare BOMs between two revisions
   */
  compareBomByRevision(partId: string, fromRevision: string, toRevision: string): Observable<BomDifference[]> {
    const params = new HttpParams()
      .set('fromRevision', fromRevision)
      .set('toRevision', toRevision);
    return this.http.get<BomDifference[]>(`${this.apiUrl}/Bom/parts/${partId}/compare`, { params });
  }

  /**
   * Compare BOMs between two versions
   */
  compareBomByVersion(fromVersionId: string, toVersionId: string): Observable<BomDifference[]> {
    const params = new HttpParams()
      .set('fromVersionId', fromVersionId)
      .set('toVersionId', toVersionId);
    return this.http.get<BomDifference[]>(`${this.apiUrl}/Bom/versions/compare`, { params });
  }

  /**
   * Validate the BOM structure for a part
   */
  validateBom(partId: string): Observable<BomValidationResult> {
    return this.http.get<BomValidationResult>(`${this.apiUrl}/Bom/parts/${partId}/validate`);
  }

  /**
   * Check if adding a child would create a circular reference
   */
  checkBomCycle(parentPartId: string, childPartId: string): Observable<{ wouldCreateCycle: boolean }> {
    return this.http.get<{ wouldCreateCycle: boolean }>(`${this.apiUrl}/Bom/parts/${parentPartId}/check-cycle/${childPartId}`);
  }

  /**
   * Copy BOM from one version to another
   */
  copyBom(sourceVersionId: string, targetVersionId: string): Observable<{ copiedCount: number }> {
    return this.http.post<{ copiedCount: number }>(`${this.apiUrl}/Bom/versions/${sourceVersionId}/copy-to/${targetVersionId}`, {});
  }

  /**
   * Copy BOM from one part to another
   */
  copyBomToPart(sourcePartId: string, targetPartId: string): Observable<{ copiedCount: number }> {
    return this.http.post<{ copiedCount: number }>(`${this.apiUrl}/Bom/parts/${sourcePartId}/copy-to/${targetPartId}`, {});
  }

  /**
   * Create a new Part
   */
  createPart(request: CreatePartRequest): Observable<Part> {
    return this.http.post<Part>(`${this.apiUrl}/Parts`, request);
  }

  /**
   * Get latest version for a Part
   */
  getLatestPartVersion(partId: string): Observable<PartVersion> {
    return this.http.get<PartVersion>(`${this.apiUrl}/Parts/${partId}/versions/latest`);
  }

  /**
   * Attach a file to a Part version
   */
  attachFileToPartVersion(versionId: string, file: File, description?: string): Observable<FileVaultItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return this.http.post<FileVaultItem>(`${this.apiUrl}/Parts/versions/${versionId}/files`, formData);
  }

  /**
   * Get files for a Part version
   */
  getPartVersionFiles(versionId: string): Observable<FileVaultItem[]> {
    return this.http.get<FileVaultItem[]>(`${this.apiUrl}/Parts/versions/${versionId}/files`);
  }

  /**
   * Create a new Document
   */
  createDocument(request: CreateDocumentRequest): Observable<Document> {
    return this.http.post<Document>(`${this.apiUrl}/Documents`, request);
  }

  /**
   * Get latest version for a Document
   */
  getLatestDocumentVersion(documentId: string): Observable<DocumentVersion> {
    return this.http.get<DocumentVersion>(`${this.apiUrl}/Documents/${documentId}/latest-version`);
  }

  /**
   * Attach a file to a Document version
   */
  attachFileToDocumentVersion(versionId: string, file: File, description?: string): Observable<FileVaultItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return this.http.post<FileVaultItem>(`${this.apiUrl}/Documents/versions/${versionId}/files`, formData);
  }

  /**
   * Get files for a Document version
   */
  getDocumentVersionFiles(versionId: string): Observable<FileVaultItem[]> {
    return this.http.get<FileVaultItem[]>(`${this.apiUrl}/Documents/versions/${versionId}/files`);
  }

  /**
   * Get documents linked to a Part
   */
  getLinkedDocuments(partId: string): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.apiUrl}/Parts/${partId}/documents`);
  }

  /**
   * Link a document to a Part
   */
  linkDocumentToPart(partId: string, request: LinkDocumentRequest): Observable<PartDocumentLink> {
    return this.http.post<PartDocumentLink>(`${this.apiUrl}/Parts/${partId}/documents`, request);
  }

  /**
   * Unlink a document from a Part
   */
  unlinkDocumentFromPart(partId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Parts/${partId}/documents/${documentId}`);
  }

  /**
   * Upload files to a document by drawing number.
   * Resolves Part by partId (preferred) or partNumber fallback.
   * Filenames must contain the drawing number.
   */
  uploadByDrawingNumber(drawingNumber: string, partId: string | null, partNumber: string | null, files: File[]): Observable<DocumentUploadResult> {
    const formData = new FormData();
    formData.append('drawingNumber', drawingNumber);
    if (partId) {
      formData.append('partId', partId);
    }
    if (partNumber) {
      formData.append('partNumber', partNumber);
    }
    files.forEach(f => formData.append('files', f, f.name));
    return this.http.post<DocumentUploadResult>(`${this.apiUrl}/Documents/upload-by-drawing`, formData);
  }

  /**
   * Get parts linked to a Document
   */
  getDocumentLinkedParts(documentId: string): Observable<Part[]> {
    return this.http.get<Part[]>(`${this.apiUrl}/Documents/${documentId}/parts`);
  }

  /**
   * Get parts linked to a File (via document associations)
   */
  getFileLinkedParts(fileId: string): Observable<Part[]> {
    return this.http.get<Part[]>(`${this.apiUrl}/Files/${fileId}/linked-parts`);
  }

  /**
   * Link BOM documents to a part (creates documents and part if they don't exist)
   */
  linkBomDocumentsToPart(request: LinkDocumentsToPartRequest): Observable<LinkDocumentsToPartResponse> {
    return this.http.post<LinkDocumentsToPartResponse>(`${this.apiUrl}/Documents/link-bom-documents`, request);
  }

  // ============ Change Management Operations ============

  /**
   * Search changes
   */
  searchChanges(criteria: ChangeSearchCriteria): Observable<PagedResult<Change>> {
    let params = new HttpParams();

    if (criteria.searchText) params = params.set('searchText', criteria.searchText);
    if (criteria.changeNumber) params = params.set('changeNumber', criteria.changeNumber);
    if (criteria.changeType !== undefined) params = params.set('changeType', criteria.changeType.toString());
    if (criteria.state !== undefined) params = params.set('state', criteria.state.toString());
    if (criteria.states?.length) params = params.set('states', criteria.states.join(','));
    if (criteria.priority !== undefined) params = params.set('priority', criteria.priority.toString());
    if (criteria.raisedBy) params = params.set('raisedBy', criteria.raisedBy);
    if (criteria.assigneeId !== undefined) params = params.set('assigneeId', criteria.assigneeId.toString());
    if (criteria.raisedAfter) params = params.set('raisedAfter', criteria.raisedAfter.toISOString());
    if (criteria.raisedBefore) params = params.set('raisedBefore', criteria.raisedBefore.toISOString());
    if (criteria.dueAfter) params = params.set('dueAfter', criteria.dueAfter.toISOString());
    if (criteria.dueBefore) params = params.set('dueBefore', criteria.dueBefore.toISOString());
    if (criteria.page) params = params.set('page', criteria.page.toString());
    if (criteria.pageSize) params = params.set('pageSize', criteria.pageSize.toString());
    if (criteria.sortBy) params = params.set('sortBy', criteria.sortBy);
    if (criteria.sortDescending !== undefined) params = params.set('sortDescending', criteria.sortDescending.toString());

    return this.http.get<PagedResult<Change>>(`${this.apiUrl}/Changes`, { params });
  }

  /**
   * Get change by ID
   */
  getChange(id: string, includeDetails = false): Observable<Change> {
    const params = new HttpParams().set('includeDetails', includeDetails.toString());
    return this.http.get<Change>(`${this.apiUrl}/Changes/${id}`, { params });
  }

  /**
   * Get change by change number
   */
  getChangeByNumber(changeNumber: string): Observable<Change> {
    return this.http.get<Change>(`${this.apiUrl}/Changes/by-number/${changeNumber}`);
  }

  /**
   * Create a new change
   */
  createChange(request: CreateChangeRequest): Observable<Change> {
    return this.http.post<Change>(`${this.apiUrl}/Changes`, request);
  }

  /**
   * Update a change
   */
  updateChange(id: string, request: UpdateChangeRequest): Observable<Change> {
    return this.http.put<Change>(`${this.apiUrl}/Changes/${id}`, request);
  }

  /**
   * Delete a change (draft only)
   */
  deleteChange(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Changes/${id}`);
  }

  // ============ Affected Items ============

  /**
   * Get affected items for a change
   */
  getAffectedItems(changeId: string): Observable<ChangeItem[]> {
    return this.http.get<ChangeItem[]>(`${this.apiUrl}/Changes/${changeId}/items`);
  }

  /**
   * Add an affected item to a change
   */
  addAffectedItem(changeId: string, request: AddChangeItemRequest): Observable<ChangeItem> {
    return this.http.post<ChangeItem>(`${this.apiUrl}/Changes/${changeId}/items`, request);
  }

  /**
   * Update an affected item
   */
  updateAffectedItem(changeId: string, itemId: string, request: UpdateChangeItemRequest): Observable<ChangeItem> {
    return this.http.put<ChangeItem>(`${this.apiUrl}/Changes/${changeId}/items/${itemId}`, request);
  }

  /**
   * Remove an affected item
   */
  removeAffectedItem(changeId: string, itemId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Changes/${changeId}/items/${itemId}`);
  }

  // ============ State Transitions ============

  /**
   * Get allowed state transitions for a change
   */
  getAllowedChangeTransitions(changeId: string): Observable<ChangeState[]> {
    return this.http.get<ChangeState[]>(`${this.apiUrl}/Changes/${changeId}/transitions`);
  }

  /**
   * Submit a change for review
   */
  submitChange(changeId: string, request?: SubmitChangeRequest): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/submit`, request || {});
  }

  /**
   * Approve a change
   */
  approveChange(changeId: string, request?: ApproveChangeRequest): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/approve`, request || {});
  }

  /**
   * Reject a change
   */
  rejectChange(changeId: string, request: RejectChangeRequest): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/reject`, request);
  }

  /**
   * Start implementation of a change
   */
  startImplementation(changeId: string, request?: StartImplementationRequest): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/start-implementation`, request || {});
  }

  /**
   * Mark a change as implemented
   */
  implementChange(changeId: string, request?: ImplementChangeRequest): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/implement`, request || {});
  }

  /**
   * Close a change
   */
  closeChange(changeId: string, request?: CloseChangeRequest): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/close`, request || {});
  }

  /**
   * Cancel a change
   */
  cancelChange(changeId: string, reason: string): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/cancel`, { reason });
  }

  /**
   * Reopen a rejected change
   */
  reopenChange(changeId: string): Observable<ChangeTransitionResult> {
    return this.http.post<ChangeTransitionResult>(`${this.apiUrl}/Changes/${changeId}/reopen`, {});
  }

  // ============ ECR/ECO Operations ============

  /**
   * Convert an ECR to an ECO
   */
  convertToEco(changeId: string, request?: ConvertToEcoRequest): Observable<Change> {
    return this.http.post<Change>(`${this.apiUrl}/Changes/${changeId}/convert-to-eco`, request || {});
  }

  /**
   * Get changes affecting a specific entity
   */
  getChangesForEntity(entityType: string, entityId: string): Observable<Change[]> {
    return this.http.get<Change[]>(`${this.apiUrl}/Changes/for-entity/${entityType}/${entityId}`);
  }

  // ============ Dashboard ============

  /**
   * Get change counts by state
   */
  getChangeCountByState(): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(`${this.apiUrl}/Changes/dashboard/by-state`);
  }

  /**
   * Get overdue changes
   */
  getOverdueChanges(): Observable<Change[]> {
    return this.http.get<Change[]>(`${this.apiUrl}/Changes/dashboard/overdue`);
  }

  // ============ Common Directory Operations ============

  getCommonUsers(activeOnly = true): Observable<CommonUser[]> {
    const params = new HttpParams().set('activeOnly', activeOnly.toString());
    return this.http.get<CommonUser[]>(`${this.apiUrl}/common/users`, { params });
  }

  getCommonUserDetail(userId: number): Observable<CommonUserDetail> {
    return this.http.get<CommonUserDetail>(`${this.apiUrl}/common/users/${userId}`);
  }

  getCommonRoles(): Observable<CommonRole[]> {
    return this.http.get<CommonRole[]>(`${this.apiUrl}/common/roles`);
  }

  getCommonPermissions(): Observable<CommonPermission[]> {
    return this.http.get<CommonPermission[]>(`${this.apiUrl}/common/permissions`);
  }

  updateCommonUserRole(userId: number, request: UpdateUserRoleRequest): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/common/users/${userId}/role`, request);
  }

  updateCommonUserPermissions(userId: number, request: UpdateUserPermissionsRequest): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/common/users/${userId}/permissions`, request);
  }

  /**
   * Get my assigned changes
   */
  getMyAssignedChanges(userId: number): Observable<Change[]> {
    const params = new HttpParams().set('userId', userId.toString());
    return this.http.get<Change[]>(`${this.apiUrl}/Changes/my-assigned`, { params });
  }

  /**
   * Get my raised changes
   */
  getMyRaisedChanges(userName: string): Observable<Change[]> {
    const params = new HttpParams().set('userName', userName);
    return this.http.get<Change[]>(`${this.apiUrl}/Changes/my-raised`, { params });
  }

  // ============ Notification Operations ============

  /**
   * Get user notifications with filtering and pagination
   */
  getNotifications(criteria?: NotificationSearchCriteria): Observable<NotificationSearchResult> {
    let params = new HttpParams();

    if (criteria) {
      if (criteria.type !== undefined) params = params.set('type', criteria.type.toString());
      if (criteria.priority !== undefined) params = params.set('priority', criteria.priority.toString());
      if (criteria.isRead !== undefined) params = params.set('isRead', criteria.isRead.toString());
      if (criteria.isDismissed !== undefined) params = params.set('isDismissed', criteria.isDismissed.toString());
      if (criteria.entityType) params = params.set('entityType', criteria.entityType);
      if (criteria.entityId) params = params.set('entityId', criteria.entityId);
      if (criteria.createdFrom) params = params.set('createdFrom', criteria.createdFrom.toISOString());
      if (criteria.createdTo) params = params.set('createdTo', criteria.createdTo.toISOString());
      if (criteria.includeExpired !== undefined) params = params.set('includeExpired', criteria.includeExpired.toString());
      if (criteria.pageNumber) params = params.set('pageNumber', criteria.pageNumber.toString());
      if (criteria.pageSize) params = params.set('pageSize', criteria.pageSize.toString());
    }

    return this.http.get<NotificationSearchResult>(`${this.apiUrl}/Notifications`, { params });
  }

  /**
   * Get a specific notification by ID
   */
  getNotification(id: string): Observable<Notification> {
    return this.http.get<Notification>(`${this.apiUrl}/Notifications/${id}`);
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.apiUrl}/Notifications/unread/count`);
  }

  /**
   * Get notification summary (unread, urgent, recent)
   */
  getNotificationSummary(): Observable<NotificationSummary> {
    return this.http.get<NotificationSummary>(`${this.apiUrl}/Notifications/summary`);
  }

  /**
   * Mark a notification as read
   */
  markAsRead(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/Notifications/${id}/read`, {});
  }

  /**
   * Mark multiple notifications as read
   */
  markAllAsRead(notificationIds?: string[]): Observable<{ count: number }> {
    const request: MarkNotificationsReadRequest = { notificationIds };
    return this.http.post<{ count: number }>(`${this.apiUrl}/Notifications/read-all`, request);
  }

  /**
   * Dismiss a notification
   */
  dismissNotification(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/Notifications/${id}/dismiss`, {});
  }

  /**
   * Delete a notification
   */
  deleteNotification(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Notifications/${id}`);
  }

  // ============ Notification Preferences ============

  /**
   * Get user notification preferences
   */
  getNotificationPreferences(): Observable<UserNotificationPreference[]> {
    return this.http.get<UserNotificationPreference[]>(`${this.apiUrl}/Notifications/preferences`);
  }

  /**
   * Update user notification preferences
   */
  updateNotificationPreferences(request: UpdateNotificationPreferencesRequest): Observable<UserNotificationPreference[]> {
    return this.http.put<UserNotificationPreference[]>(`${this.apiUrl}/Notifications/preferences`, request);
  }

  // ============ Webhooks ============

  /**
   * Get all webhooks
   */
  getWebhooks(): Observable<Webhook[]> {
    return this.http.get<Webhook[]>(`${this.apiUrl}/Notifications/webhooks`);
  }

  /**
   * Get a specific webhook
   */
  getWebhook(id: string): Observable<Webhook> {
    return this.http.get<Webhook>(`${this.apiUrl}/Notifications/webhooks/${id}`);
  }

  /**
   * Create a new webhook
   */
  createWebhook(request: CreateWebhookRequest): Observable<Webhook> {
    return this.http.post<Webhook>(`${this.apiUrl}/Notifications/webhooks`, request);
  }

  /**
   * Update a webhook
   */
  updateWebhook(id: string, request: UpdateWebhookRequest): Observable<Webhook> {
    return this.http.put<Webhook>(`${this.apiUrl}/Notifications/webhooks/${id}`, request);
  }

  /**
   * Delete a webhook
   */
  deleteWebhook(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Notifications/webhooks/${id}`);
  }

  /**
   * Get webhook delivery history
   */
  getWebhookDeliveries(webhookId: string): Observable<WebhookDelivery[]> {
    return this.http.get<WebhookDelivery[]>(`${this.apiUrl}/Notifications/webhooks/${webhookId}/deliveries`);
  }

  // ============ Notification Templates ============

  /**
   * Get all notification templates
   */
  getNotificationTemplates(): Observable<NotificationTemplate[]> {
    return this.http.get<NotificationTemplate[]>(`${this.apiUrl}/Notifications/templates`);
  }

  /**
   * Get template by event type
   */
  getNotificationTemplate(eventType: string): Observable<NotificationTemplate> {
    return this.http.get<NotificationTemplate>(`${this.apiUrl}/Notifications/templates/${eventType}`);
  }

  // ============ Admin Notification Operations ============

  /**
   * Send a notification (admin only)
   */
  sendNotification(request: SendNotificationRequest): Observable<{ sent: number }> {
    return this.http.post<{ sent: number }>(`${this.apiUrl}/Notifications/send`, request);
  }

  /**
   * Cleanup old notifications
   */
  cleanupNotifications(olderThanDays?: number): Observable<{ deleted: number }> {
    let params = new HttpParams();
    if (olderThanDays) params = params.set('olderThanDays', olderThanDays.toString());
    return this.http.post<{ deleted: number }>(`${this.apiUrl}/Notifications/cleanup`, {}, { params });
  }

  // ============ Search Operations ============

  /**
   * Unified search across all entity types
   */
  search(criteria: SearchCriteria): Observable<SearchResults> {
    let params = new HttpParams();

    if (criteria.query) params = params.set('q', criteria.query);
    if (criteria.entityType !== undefined) params = params.set('entityType', criteria.entityType.toString());
    if (criteria.lifecycleState !== undefined) params = params.set('lifecycleState', criteria.lifecycleState.toString());
    if (criteria.createdAfter) params = params.set('createdAfter', criteria.createdAfter.toISOString());
    if (criteria.createdBefore) params = params.set('createdBefore', criteria.createdBefore.toISOString());
    if (criteria.createdBy) params = params.set('createdBy', criteria.createdBy);
    if (criteria.partType) params = params.set('partType', criteria.partType);
    if (criteria.documentType) params = params.set('documentType', criteria.documentType);
    if (criteria.project) params = params.set('project', criteria.project);
    if (criteria.latestVersionOnly !== undefined) params = params.set('latestVersionOnly', criteria.latestVersionOnly.toString());
    if (criteria.page) params = params.set('page', criteria.page.toString());
    if (criteria.pageSize) params = params.set('pageSize', criteria.pageSize.toString());
    if (criteria.sortBy) params = params.set('sortBy', criteria.sortBy);
    if (criteria.sortDescending !== undefined) params = params.set('sortDescending', criteria.sortDescending.toString());

    return this.http.get<SearchResults>(`${this.apiUrl}/Search`, { params });
  }

  /**
   * Search parts only
   */
  searchParts(query: string, page = 1, pageSize = 20): Observable<SearchResults> {
    const params = new HttpParams()
      .set('q', query)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<SearchResults>(`${this.apiUrl}/Search/parts`, { params });
  }

  /**
   * Search documents only
   */
  searchDocuments(query: string, page = 1, pageSize = 20): Observable<SearchResults> {
    const params = new HttpParams()
      .set('q', query)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<SearchResults>(`${this.apiUrl}/Search/documents`, { params });
  }

  /**
   * Search files only
   */
  searchFilesUnified(query: string, page = 1, pageSize = 20): Observable<SearchResults> {
    const params = new HttpParams()
      .set('q', query)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<SearchResults>(`${this.apiUrl}/Search/files`, { params });
  }

  /**
   * Search changes only
   */
  searchChangesUnified(query: string, page = 1, pageSize = 20): Observable<SearchResults> {
    const params = new HttpParams()
      .set('q', query)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<SearchResults>(`${this.apiUrl}/Search/changes`, { params });
  }

  /**
   * Get search suggestions/autocomplete
   */
  getSearchSuggestions(query: string, entityType?: SearchEntityType, limit = 10): Observable<SearchSuggestion[]> {
    let params = new HttpParams()
      .set('q', query)
      .set('limit', limit.toString());
    if (entityType !== undefined) params = params.set('entityType', entityType.toString());
    return this.http.get<SearchSuggestion[]>(`${this.apiUrl}/Search/suggest`, { params });
  }

  /**
   * Get facet counts for search results
   */
  getSearchFacets(query: string): Observable<{ entityTypeFacets: Record<string, number>; stateFacets: Record<string, number>; subTypeFacets: Record<string, number> }> {
    const params = new HttpParams().set('q', query);
    return this.http.get<{ entityTypeFacets: Record<string, number>; stateFacets: Record<string, number>; subTypeFacets: Record<string, number> }>(`${this.apiUrl}/Search/facets`, { params });
  }

  /**
   * Get recent searches for the current user
   */
  getRecentSearches(): Observable<RecentSearch[]> {
    return this.http.get<RecentSearch[]>(`${this.apiUrl}/Search/recent`);
  }

  /**
   * Clear recent searches
   */
  clearRecentSearches(): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Search/recent`);
  }

  /**
   * Get search status/capabilities
   */
  getSearchStatus(): Observable<{ fullTextEnabled: boolean; indexedCount: number }> {
    return this.http.get<{ fullTextEnabled: boolean; indexedCount: number }>(`${this.apiUrl}/Search/status`);
  }

  // ============ Saved Search Operations ============

  /**
   * Get all saved searches for the current user
   */
  getSavedSearches(): Observable<SavedSearch[]> {
    return this.http.get<SavedSearch[]>(`${this.apiUrl}/Search/saved`);
  }

  /**
   * Get a specific saved search by ID
   */
  getSavedSearch(id: string): Observable<SavedSearch> {
    return this.http.get<SavedSearch>(`${this.apiUrl}/Search/saved/${id}`);
  }

  /**
   * Save a new search
   */
  saveSearch(name: string, criteria: SearchCriteria, isShared = false): Observable<SavedSearch> {
    return this.http.post<SavedSearch>(`${this.apiUrl}/Search/saved`, {
      name,
      query: criteria.query,
      criteria,
      isShared
    });
  }

  /**
   * Update a saved search
   */
  updateSavedSearch(id: string, name: string, criteria: SearchCriteria, isShared: boolean): Observable<SavedSearch> {
    return this.http.put<SavedSearch>(`${this.apiUrl}/Search/saved/${id}`, {
      name,
      query: criteria.query,
      criteria,
      isShared
    });
  }

  /**
   * Delete a saved search
   */
  deleteSavedSearch(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Search/saved/${id}`);
  }

  /**
   * Share/unshare a saved search
   */
  toggleSavedSearchSharing(id: string, isShared: boolean): Observable<SavedSearch> {
    return this.http.patch<SavedSearch>(`${this.apiUrl}/Search/saved/${id}/share`, { isShared });
  }

  // ============ Workflow Template Operations ============

  /**
   * Get all workflow templates
   */
  getWorkflowTemplates(activeOnly = true): Observable<WorkflowTemplate[]> {
    const params = new HttpParams().set('activeOnly', activeOnly.toString());
    return this.http.get<WorkflowTemplate[]>(`${this.apiUrl}/Workflows/templates`, { params });
  }

  /**
   * Get workflow templates for a specific entity type
   */
  getWorkflowTemplatesForEntity(entityType: string): Observable<WorkflowTemplate[]> {
    return this.http.get<WorkflowTemplate[]>(`${this.apiUrl}/Workflows/templates/for-entity/${entityType}`);
  }

  /**
   * Get a specific workflow template
   */
  getWorkflowTemplate(id: string): Observable<WorkflowTemplate> {
    return this.http.get<WorkflowTemplate>(`${this.apiUrl}/Workflows/templates/${id}`);
  }

  /**
   * Create a new workflow template
   */
  createWorkflowTemplate(request: CreateWorkflowTemplateRequest): Observable<WorkflowTemplate> {
    return this.http.post<WorkflowTemplate>(`${this.apiUrl}/Workflows/templates`, request);
  }

  /**
   * Update a workflow template
   */
  updateWorkflowTemplate(id: string, request: UpdateWorkflowTemplateRequest): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(`${this.apiUrl}/Workflows/templates/${id}`, request);
  }

  /**
   * Delete a workflow template
   */
  deleteWorkflowTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Workflows/templates/${id}`);
  }

  // ============ Workflow Instance Operations ============

  /**
   * Search workflow instances
   */
  searchWorkflows(criteria: WorkflowSearchCriteria): Observable<PagedResult<WorkflowInstance>> {
    let params = new HttpParams();

    if (criteria.templateId) params = params.set('templateId', criteria.templateId);
    if (criteria.entityType) params = params.set('entityType', criteria.entityType);
    if (criteria.entityId) params = params.set('entityId', criteria.entityId);
    if (criteria.state !== undefined) params = params.set('state', criteria.state.toString());
    if (criteria.startedBy) params = params.set('startedBy', criteria.startedBy);
    if (criteria.startedFrom) params = params.set('startedFrom', criteria.startedFrom.toISOString());
    if (criteria.startedTo) params = params.set('startedTo', criteria.startedTo.toISOString());
    if (criteria.pageNumber) params = params.set('pageNumber', criteria.pageNumber.toString());
    if (criteria.pageSize) params = params.set('pageSize', criteria.pageSize.toString());

    return this.http.get<PagedResult<WorkflowInstance>>(`${this.apiUrl}/Workflows`, { params });
  }

  /**
   * Get a specific workflow instance
   */
  getWorkflow(id: string, includeDetails = true): Observable<WorkflowInstance> {
    const params = new HttpParams().set('includeDetails', includeDetails.toString());
    return this.http.get<WorkflowInstance>(`${this.apiUrl}/Workflows/${id}`, { params });
  }

  /**
   * Get active workflow for an entity
   */
  getWorkflowForEntity(entityType: string, entityId: string): Observable<WorkflowInstance> {
    return this.http.get<WorkflowInstance>(`${this.apiUrl}/Workflows/for-entity/${entityType}/${entityId}`);
  }

  /**
   * Get all workflows for an entity
   */
  getAllWorkflowsForEntity(entityType: string, entityId: string): Observable<WorkflowInstance[]> {
    return this.http.get<WorkflowInstance[]>(`${this.apiUrl}/Workflows/for-entity/${entityType}/${entityId}/all`);
  }

  /**
   * Start a new workflow
   */
  startWorkflow(request: StartWorkflowRequest): Observable<StartWorkflowResult> {
    return this.http.post<StartWorkflowResult>(`${this.apiUrl}/Workflows/start`, request);
  }

  /**
   * Get workflow progress
   */
  getWorkflowProgress(instanceId: string): Observable<WorkflowProgress> {
    return this.http.get<WorkflowProgress>(`${this.apiUrl}/Workflows/${instanceId}/progress`);
  }

  /**
   * Cancel a workflow
   */
  cancelWorkflow(instanceId: string, request: CancelWorkflowRequest): Observable<WorkflowStepResult> {
    return this.http.post<WorkflowStepResult>(`${this.apiUrl}/Workflows/${instanceId}/cancel`, request);
  }

  // ============ Workflow Step Operations ============

  /**
   * Get current step of a workflow
   */
  getCurrentStep(instanceId: string): Observable<WorkflowStep> {
    return this.http.get<WorkflowStep>(`${this.apiUrl}/Workflows/${instanceId}/current-step`);
  }

  /**
   * Get a specific workflow step
   */
  getWorkflowStep(stepId: string): Observable<WorkflowStep> {
    return this.http.get<WorkflowStep>(`${this.apiUrl}/Workflows/steps/${stepId}`);
  }

  /**
   * Approve a workflow step
   */
  approveStep(stepId: string, request?: ApproveStepRequest): Observable<WorkflowStepResult> {
    return this.http.post<WorkflowStepResult>(`${this.apiUrl}/Workflows/steps/${stepId}/approve`, request || {});
  }

  /**
   * Reject a workflow step
   */
  rejectStep(stepId: string, request: RejectStepRequest): Observable<WorkflowStepResult> {
    return this.http.post<WorkflowStepResult>(`${this.apiUrl}/Workflows/steps/${stepId}/reject`, request);
  }

  /**
   * Delegate a workflow step
   */
  delegateStep(stepId: string, request: DelegateStepRequest): Observable<WorkflowStepResult> {
    return this.http.post<WorkflowStepResult>(`${this.apiUrl}/Workflows/steps/${stepId}/delegate`, request);
  }

  /**
   * Skip a workflow step
   */
  skipStep(stepId: string, request?: SkipStepRequest): Observable<WorkflowStepResult> {
    return this.http.post<WorkflowStepResult>(`${this.apiUrl}/Workflows/steps/${stepId}/skip`, request || {});
  }

  /**
   * Claim a workflow step for the current user
   */
  claimStep(stepId: string, request?: ClaimStepRequest, loginId?: string): Observable<WorkflowStepResult> {
    const headers = loginId ? { 'X-LoginId': loginId } : undefined;
    return this.http.post<WorkflowStepResult>(
      `${this.apiUrl}/Workflows/steps/${stepId}/claim`,
      request || {},
      { headers }
    );
  }

  /**
   * Add a step to a running workflow instance
   */
  addWorkflowStep(instanceId: string, request: AddWorkflowStepRequest): Observable<WorkflowStepResult> {
    return this.http.post<WorkflowStepResult>(`${this.apiUrl}/Workflows/${instanceId}/steps`, request);
  }

  /**
   * Remove a pending step from a running workflow instance
   */
  removeWorkflowStep(instanceId: string, stepId: string, request?: RemoveWorkflowStepRequest): Observable<WorkflowStepResult> {
    return this.http.delete<WorkflowStepResult>(`${this.apiUrl}/Workflows/${instanceId}/steps/${stepId}`, {
      body: request || {}
    });
  }

  // ============ Workflow Task Operations ============

  /**
   * Get my pending tasks
   */
  getMyTasks(userName = 'system'): Observable<WorkflowTask[]> {
    const params = new HttpParams().set('userName', userName);
    return this.http.get<WorkflowTask[]>(`${this.apiUrl}/Workflows/my-tasks`, { params });
  }

  /**
   * Get pending task count
   */
  getMyTaskCount(userName = 'system'): Observable<{ count: number }> {
    const params = new HttpParams().set('userName', userName);
    return this.http.get<{ count: number }>(`${this.apiUrl}/Workflows/my-tasks/count`, { params });
  }

  /**
   * Get tasks by role
   */
  getTasksByRole(roleName: string): Observable<WorkflowTask[]> {
    return this.http.get<WorkflowTask[]>(`${this.apiUrl}/Workflows/tasks/by-role/${roleName}`);
  }

  /**
   * Search tasks
   */
  searchTasks(criteria: WorkflowTaskSearchCriteria): Observable<PagedResult<WorkflowTask>> {
    let params = new HttpParams();

    if (criteria.assignedTo) params = params.set('assignedTo', criteria.assignedTo);
    if (criteria.assignedToRole) params = params.set('assignedToRole', criteria.assignedToRole);
    if (criteria.state !== undefined) params = params.set('state', criteria.state.toString());
    if (criteria.includeOverdue !== undefined) params = params.set('includeOverdue', criteria.includeOverdue.toString());
    if (criteria.dueBefore) params = params.set('dueBefore', criteria.dueBefore.toISOString());
    if (criteria.entityType) params = params.set('entityType', criteria.entityType);
    if (criteria.pageNumber) params = params.set('pageNumber', criteria.pageNumber.toString());
    if (criteria.pageSize) params = params.set('pageSize', criteria.pageSize.toString());

    return this.http.get<PagedResult<WorkflowTask>>(`${this.apiUrl}/Workflows/tasks`, { params });
  }

  /**
   * Get overdue tasks
   */
  getOverdueTasks(): Observable<WorkflowTask[]> {
    return this.http.get<WorkflowTask[]>(`${this.apiUrl}/Workflows/tasks/overdue`);
  }

  // ============ Workflow Dashboard ============

  /**
   * Get workflow count by state
   */
  getWorkflowCountByState(): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(`${this.apiUrl}/Workflows/dashboard/by-state`);
  }
}

